export interface ReleaseNoteSection {
	/** Módulo ou área da mudança */
	title: string
	items: string[]
}

export interface ReleaseNote {
	version: string
	/** YYYY-MM-DD */
	date: string
	summary: string
	sections: ReleaseNoteSection[]
}

/**
 * Novidades exibidas em Administração > Novidades, da versão mais recente para a mais antiga.
 * Texto informativo para quem administra o SSMA: escreva o que mudou para o usuário, não o detalhe técnico.
 */
export const RELEASE_NOTES: ReleaseNote[] = [
	{
		version: '1.3.0',
		date: '2026-10-21',
		summary:
			'Nova tela de Exportação Sphera, Cadastros Sphera com parâmetros editáveis, campo "Flag do Sphera" nos cadastros de Tipologia e Classificação e correções da câmera.',
		sections: [
			{
				title: 'Exportação Sphera',
				items: [
					'Em Organização e acessos > Exportação Sphera (somente administradores): escolha o mês, os tipos (Ver e Agir, Interações, Cartão Amarelo) e a filial e baixe a planilha no formato de importação do Sphera.',
					'A prévia é opcional: fica desligada por padrão (o navegador lembra da sua escolha). Ligada, cada registro aparece em uma linha que expande para os 27 campos, e a distribuição por flag pode ser recolhida.',
					'Registros com configuração pendente aparecem em destaque. Na própria tela é possível definir o Specific Service de cada CR e o nome da filial no Sphera.',
					'Quando o mês tem mais linhas que o limite por arquivo (2000 por padrão, que é o máximo aceito pelo Sphera), o botão de baixar vira um menu: baixar todos os arquivos em sequência ou cada arquivo separadamente.',
				],
			},
			{
				title: 'Cadastros Sphera',
				items: [
					'Em Organização e acessos > Cadastros Sphera, as abas Flags, Serviços, Filiais e CRs concentram tudo o que o Sphera aceita, com busca e filtro de configurado/pendente nas listas de Filiais e CRs.',
					'Nova aba Parâmetros: mostra como cada coluna do arquivo é preenchida (origem e um exemplo real por tipo de inspeção) e permite editar os valores fixos e as regras do arquivo, inclusive o limite de linhas por arquivo. Uma barra avisa das alterações não salvas, com opções de salvar ou descartar.',
				],
			},
			{
				title: 'Cadastros',
				items: [
					'Tipologias (Cartão Amarelo / Ver e Agir) e Classificações (Interação de Segurança) ganharam o campo "Flag do Sphera", usado na coluna Flags Categories da exportação.',
				],
			},
			{
				title: 'Câmera',
				items: [
					'O botão de leitura de QR Code ganhou um indicador: verde (câmera liberada), amarelo (permissão pendente) ou vermelho (sem permissão).',
					'Com a permissão pendente, o scanner mostra o botão "Permitir câmera". Toque nele para o aparelho exibir o aviso de permissão (no iPhone o aviso nem sempre aparecia sozinho).',
					'Se a câmera estiver bloqueada, a tela explica como liberar de acordo com o navegador: no Safari do iPhone, aA > Ajustes do Site > Câmera; no Chrome, Firefox ou Edge, Ajustes do iPhone > navegador > Câmera.',
					'No celular, a leitura do crachá agora abre em uma janela centralizada, no lugar do painel que subia da parte de baixo, o que evita falhas ao liberar a câmera no iPhone.',
					'Novo item "Câmera" no menu do usuário, com o status atual e a opção de solicitar a permissão.',
				],
			},
		],
	},
	{
		version: '1.2.3',
		date: '2026-10-21',
		summary:
			'Campanhas passam a se chamar Questionários, com exclusão de respostas, e a leitura de QR Code fica mais clara quando a câmera falha.',
		sections: [
			{
				title: 'Questionários',
				items: [
					'O módulo "Campanhas" foi renomeado para "Questionários" em todo o sistema (menus, telas e mensagens). Os links já compartilhados continuam funcionando.',
					'Excluir resposta: em Resultados > Respostas individuais, cada resposta tem agora um botão de lixeira. Use quando alguém responder errado: a resposta (e os arquivos enviados) some dos resultados e da exportação, e a pessoa pode responder de novo. A exclusão não pode ser desfeita.',
				],
			},
			{
				title: 'DDS e leitura de QR Code',
				items: [
					'Quando a câmera não abre ao escanear o crachá ou o QR de presença (comum no iPhone), a janela não fecha mais sozinha: aparece uma mensagem explicando o motivo e o botão "Tentar novamente".',
					'Se a câmera estiver ocupada por outro app ou aba, a mensagem orienta a fechá-los e tentar de novo.',
				],
			},
		],
	},
	{
		version: '1.2.2',
		date: '2026-10-19',
		summary:
			'Ação do emociograma só para quem está triste e Direcionada de resposta livre na linha do tempo da pessoa.',
		sections: [
			{
				title: 'DDS',
				items: [
					'A ação tomada no emociograma agora só pode ser registrada para participantes que indicaram estar tristes.',
				],
			},
			{
				title: 'Matriz de Comprometimento',
				items: [
					'Nova aba "Resposta Livre" na linha do tempo da pessoa, com as respostas das Direcionadas de resposta livre.',
				],
			},
		],
	},
	{
		version: '1.2.1',
		date: '2026-10-19',
		summary: 'Notificações push no celular e no computador.',
		sections: [
			{
				title: 'Notificações',
				items: [
					'O SSMA agora pode enviar notificações push para o aparelho. Ative pelo cartão na tela inicial ou no perfil.',
					'Se as notificações estiverem bloqueadas no navegador, o cartão mostra como liberar. No iPhone, é preciso adicionar o SSMA à tela de início antes de ativar.',
				],
			},
		],
	},
	{
		version: '1.2.0',
		date: '2026-10-18',
		summary:
			'Novo módulo de Questionários, Direcionada de resposta livre, emociograma do DDS e menus reorganizados.',
		sections: [
			{
				title: 'Questionários (novo módulo)',
				items: [
					'Novo módulo para criar questionários internos de formulário e acompanhar as respostas, aberto a qualquer colaborador logado, inclusive o perfil Usuário.',
					'Como criar: em Administração > Comunicação > Questionários, clique em "Novo questionário", escreva o título, monte as perguntas, defina datas e filiais e salve. O questionário nasce como rascunho.',
					'Tipos de pergunta: texto curto e longo, Sim/Não, escolha única, múltipla escolha, nota (escala) e envio de arquivo. Cada pergunta pode ser obrigatória.',
					'Perguntas condicionais: uma pergunta só aparece conforme a resposta de uma anterior (por exemplo, "descreva o que aconteceu" só se a resposta for Sim).',
					'Como publicar: use o botão de publicar na lista e copie o link para compartilhar. Quem abrir o link entra no SSMA e responde; o questionário pode ficar disponível para todas as filiais ou só para as escolhidas, com data de início e fim.',
					'Respostas: cada pessoa responde uma vez, ou várias se o questionário permitir. Depois que chega a primeira resposta, as perguntas não podem mais ser alteradas; título, datas e filiais continuam editáveis.',
					'Resultados: participação, respostas por dia, por filial e por função, resumo de cada pergunta, quem respondeu e quem não respondeu, respostas individuais e exportação em Excel/CSV.',
					'Criar questionários e ver resultados é permitido a Administrador e Gestor SSMA.',
				],
			},
			{
				title: 'Inspeção Direcionada',
				items: [
					'Novo tipo de questionário "Resposta livre": a pessoa escreve a resposta como quiser, sem conforme/não conforme.',
					'Nova aba "Resposta Livre" nos KPIs: quem respondeu e quem não respondeu, por questionário, filial e função.',
					'Exportação das respostas em Excel/CSV (uma linha por resposta, pronta para análise com IA), para Administrador e Gestor SSMA.',
					'Questionários de resposta livre não entram nas metas da matriz nem nos KPIs de conformidade.',
				],
			},
			{
				title: 'Ver e Agir',
				items: [
					'Campo opcional "Classificação do cliente", cadastrado em Administração, com filtro no KPI.',
					'Foto da resolução ao concluir a ação.',
				],
			},
			{
				title: 'Ações em andamento',
				items: [
					'Lista em formato de tabela, com escolha das colunas visíveis.',
					'Exportação em Excel e PDF com o timbrado padrão.',
				],
			},
			{
				title: 'DDS',
				items: [
					'Emociograma com registro da ação tomada para cada emoção e filtros por emoção e por ação.',
					'Função do participante na lista de presença.',
					"PDF do DDS gerado pelo sistema, com marca d'água.",
					'Quem cria o DDS passa a contar como presente.',
				],
			},
			{
				title: 'Matriz de Comprometimento e exceções',
				items: [
					'Nova coluna "% conforme" da Direcionada, com o detalhe das respostas, e ordenação pelo total.',
					'A aba "Pendências" passou a se chamar "Justificativas" e mostra só a filial selecionada no perfil.',
				],
			},
			{
				title: 'Avisos',
				items: [
					'Carrossel com até 10 imagens por aviso.',
					'Público por perfil e por filial.',
					'Cadastro em página própria, no lugar da janela pequena.',
				],
			},
			{
				title: 'KPIs',
				items: ['Filtros de filial e CR com seleção múltipla.'],
			},
			{
				title: 'Administração e navegação',
				items: [
					'Menu da Administração agrupado (Cadastros SSMA, Comunicação, Organização e acessos, Metas e pontuação) e esta tela de Novidades.',
					'Tela inicial com os módulos separados por seção.',
					'Usuários com paginação; cards renomeados para "Cartão Amarelo / Ver e Agir", "Interação de Segurança" e "Gerenciar Unidades (TestTorque)".',
					'Botão voltar em todas as telas de criação de inspeção.',
				],
			},
		],
	},
]
